/**
 * 상세이미지 번역 문구 검사 — 번역(AI 다듬기)을 마친 글자 칸마다 소재·인증·최상급 표현을 근거와 대조한다(PR261016-11F5C4).
 * - 소재 칸(«소재: 면·린넨»)이 1688 속성 소재(폴리에스터)와 다르면 속성 값으로 바꾼다 — 공급처 이미지 문구가 속성과 다른 경우.
 * - 칸 안의 소재 낱말(«나파 가죽»)이 속성·남긴 옵션·상품 특징에 근거가 없으면 뺀다(상품명과 같은 검사 dropUnsupportedClaims).
 * - 원문(중국어)에 인증이 없는데 «인증»이 붙으면(检测 → 인증) «검사»로 되돌린다.
 * - 원문에 최상급(最·顶级·第一)이 없는데 «최상급·최고급·최고»가 붙으면 뺀다.
 * 바꾼 칸은 notes 로 돌려준다 — 화면·에이전트가 확인하도록 상품에 경고로 남긴다.
 */
// 소재 묶음·속성 소재 값은 seoSpecGuard 한 곳(MATERIALS)
import { dropUnsupportedClaims, familiesOf, attributeMaterial } from './seoSpecGuard';

export { attributeMaterial };

const LABEL_RE = /^(\s*(?:제품\s*|주요\s*|원단\s*|상품\s*)?(?:소재|재질|원단|재료|성분)\s*[:：]\s*)(.+)$/;

const CERT_SRC = /认证|证书|认可|资质|certif|(?<![A-Za-z])(?:CE|KC|FDA|SGS|ISO|3C)(?![A-Za-z])|인증/i;
const INSPECT_SRC = /检测|检验|质检|测试|验货|验/;
const SUPERLATIVE_RE = /(?:최상급|최고급|최고의|최고|최강|최상의|최상|업계\s*최초|업계\s*1위|세계\s*최초|넘버\s*원|No\.?\s*1)\s*/gi;
const SUPERLATIVE_SRC = /最|顶级|第一|极致|首创|唯一|top|best|no\.?\s*1/i;
// 원문이 优质·高级(좋은 품질)이면 최상급을 한 단계 낮춰 옮긴다 — 최고급 → 고급, 최고의 → 좋은
const QUALITY_SRC = /优质|优良|高端|高级|高品质/;
const GOOD_SRC = /好|优/;
function downgrade(word, ch) {
  const w = word.trim();
  if (/^(?:최고급|최상급)$/.test(w) && QUALITY_SRC.test(ch)) return '고급 ';
  if (/^(?:최고의|최상의)$/.test(w) && GOOD_SRC.test(ch)) return '좋은 ';
  return '';
}
// PU 소재 상품의 «가죽» → «PU 가죽» (PU 가죽·인조 가죽, 가죽 질감은 그대로)
const LEATHER_WORD = /(?<!(?:PU|인조|합성)\s*)(?:천연\s*가죽|소가죽|나파\s*가죽|가죽|레더)(?!\s*(?:질감|무늬|패턴|텍스처|느낌))/g;

function tidy(text) {
  return text.replace(/\s{2,}/g, ' ').replace(/^[\s,·/]+|[\s,·/]+$/g, '').trim();
}

/**
 * @param {{ ch: string, ko: string }} block - 원문 한 칸과 번역 문구
 * @param {{ attributes?: Array, options?: Array, productFeature?: string, targetKeyword?: string }|null} basis - 없으면 소재 검사는 건너뛴다
 * @returns {{ text: string, reasons: string[] }}
 */
export function guardDetailText(block, basis) {
  const ch = String((block && block.ch) || '');
  let text = String((block && block.ko) || '');
  const reasons = [];
  if (!text) return { text, reasons };

  // 1. 최상급 — 원문에 없으면 뺀다
  if (!SUPERLATIVE_SRC.test(ch)) {
    const found = text.match(SUPERLATIVE_RE);
    if (found) {
      const next = tidy(text.replace(SUPERLATIVE_RE, (m) => downgrade(m, ch)));
      if (next) {
        text = next;
        reasons.push(`원문에 없는 최상급 표현 «${found.map(f => f.trim()).join('·')}» 제거`);
      }
    }
  }

  // 2. 인증 — 원문이 검사(检测)면 «검사», 아니면 뺀다
  if (/인증/.test(text) && !CERT_SRC.test(ch)) {
    const to = INSPECT_SRC.test(ch) ? '검사' : '';
    const next = tidy(text.replace(/인증/g, to));
    if (next) {
      text = next;
      reasons.push(to ? '원문은 검사(检测)인데 «인증»으로 옮겨져 «검사»로 되돌림' : '원문에 없는 «인증» 제거');
    }
  }

  if (!basis) return { text, reasons };

  // 3. 소재 칸 — 속성 소재와 다르면 속성 값으로
  const attrMat = attributeMaterial(basis.attributes);
  const label = text.match(LABEL_RE);
  if (label) {
    const claimed = familiesOf(label[2]);
    const known = familiesOf(attrMat);
    if (claimed.length && known.length && claimed.some(f => !known.includes(f))) {
      const next = `${label[1]}${attrMat}`;
      reasons.push(`소재 «${label[2].trim()}»가 1688 속성 소재(${attrMat})와 달라 속성 값으로 바꿈`);
      text = next;
    }
    return { text, reasons };
  }

  // 4. 칸 안의 소재 낱말 — 속성 소재가 PU 면 «가죽»을 «PU 가죽»으로, 그 밖에 근거(속성·남긴 옵션·상품 특징·대상 키워드)에 없으면 뺀다
  const known = familiesOf(attrMat);
  if (known.includes('pu') && !known.includes('leather')) {
    const found = text.match(LEATHER_WORD);
    if (found) {
      text = text.replace(LEATHER_WORD, 'PU 가죽');
      reasons.push(`1688 속성 소재가 PU 라 «${[...new Set(found)].join('·')}»를 «PU 가죽»으로 바꿈`);
    }
  }
  const claim = dropUnsupportedClaims(text, basis, { kinds: ['material'], keepPercent: true });
  if (claim.removed.length) {
    text = claim.name;
    reasons.push(`1688 속성·옵션에 근거 없는 소재 «${[...new Set(claim.removed.map(w => w.trim()))].join('·')}» 제거`);
  }
  return { text, reasons };
}

/**
 * AI 번역 결과 배열({ch, ko:[...]})을 제자리에서 고친다.
 * @param {Array} aiResult
 * @param {Object|null} basis
 * @returns {Array<{ source: string, before: string, after: string, reasons: string[] }>} 바꾼 칸
 */
export function guardDetailTranslations(aiResult, basis) {
  const notes = [];
  (Array.isArray(aiResult) ? aiResult : []).forEach(item => {
    if (!item || !Array.isArray(item.ko) || !item.ko.length) return;
    const before = String(item.ko[0] || '');
    const r = guardDetailText({ ch: item.ch, ko: before }, basis);
    if (!r.reasons.length || r.text === before) return;
    item.ko[0] = r.text;
    notes.push({ source: item.ch, before, after: r.text, reasons: r.reasons });
  });
  return notes;
}

/** 상품에서 소재 대조 근거를 뽑는다 — 일괄 처리·재번역이 같은 근거를 쓰게 한다 */
export function claimBasisOf(product) {
  if (!product) return null;
  return { attributes: product.attributes, options: product.options, productFeature: product.productFeature, targetKeyword: product.targetKeyword };
}

/**
 * 처리된 상세이미지들의 정정 기록(claimWarnings)을 상품 경고 하나로 모은다 — 일괄 처리·편집 화면 저장·화면 표시가 같이 쓴다.
 * @param {Array} processedImages - 화면 순서대로(image = 순번+1)
 * @returns {{ message: string, items: Array, at: number }|null}
 */
export function buildClaimWarning(processedImages) {
  const items = [];
  (Array.isArray(processedImages) ? processedImages : []).forEach((img, i) => {
    ((img && img.claimWarnings) || []).forEach(w => items.push({ image: i + 1, ...w }));
  });
  if (!items.length) return null;
  return {
    message: `상세이미지 문구 ${items.length}곳을 원문·1688 속성에 맞춰 고쳤습니다(소재·인증·최상급) — 상세 이미지 단계에서 확인하세요`,
    items,
    at: Date.now()
  };
}

export default { guardDetailText, guardDetailTranslations, attributeMaterial, claimBasisOf, buildClaimWarning };
